import { useState } from 'react';
import { Description, Field } from '@headlessui/react';
import { fromJS, List, Map } from 'immutable';
import { twMerge } from 'tailwind-merge';

import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import { useModal } from 'flow/contexts/ModalProvider';

import BaseModal, { modalSizes } from 'flow/components/ui-components/BaseModal';
import InputField from 'flow/components/ui-components/InputField';
import Loading from 'flow/components/ui-components/Loading';
import Toggle from 'flow/components/ui-components/Toggle';
import ParticipantPicker from './ParticipantPicker';
import MeetingAccessDropdown from './MeetingAccessDropdown';

import { setSnackbar } from 'flow/actions/app-actions';
import { createRoomSession } from 'flow/actions/chat-actions';
import {
    fetchLobbyData,
    updateMeetingAccess,
    updateRequireHostPresent,
} from 'flow/actions/meeting-lobby-actions';
import { requestPin } from 'flow/actions/meeting-actions';
import { getMeetingCode } from 'flow/utils/meeting-utils';
import { MeetingType, MeetingAccess } from 'flow/utils/meeting-constants';
import { roomTypes } from 'flow/utils/message-utils-TW';
import { getExtensions } from 'flow/selectors/ExtensionSelectors';

import { ExtensionKey } from 'flow/types/EntityKeys';

const CreateMeetingRoomModal = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { ownExtension } = useExtension();
    const { openModal, closeModal } = useModal();
    const ownKey = ownExtension?.get('key');

    const [name, setName] = useState('');
    const [selectedKeys, setSelectedKeys] = useState<ExtensionKey[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [meetingAccess, setMeetingAccess] = useState(
        MeetingAccess.ANYONE_WITH_LINK.key,
    );
    // Toggle reads as "let people join before host" (VID-198); the persisted
    // field is the inverse, "require host present" — starts required, i.e.
    // the toggle starts off.
    const [letJoinBeforeHost, setLetJoinBeforeHost] = useState(false);

    const extensions = useAppSelector(getExtensions);

    const handleCreateError = (errorMessage: string) => {
        dispatch(
            setSnackbar({ i18nextMessage: errorMessage || 'snackbar.error' }),
        );
        setSubmitting(false);
    };

    const handleSubmit = () => {
        if (!ownExtension || submitting) return;
        setSubmitting(true);

        const trimmedName = name.trim();

        const memberMaps = selectedKeys.flatMap((key) => {
            const chatUserKey = extensions.get(key)?.get('chatUserKey');
            return chatUserKey ? [Map({ key: chatUserKey })] : [];
        });

        const chatUserKeys = List([
            ...memberMaps,
            Map({ key: ownExtension.get('chatUserKey') }),
        ]);

        const sessionDTO = fromJS({
            name: trimmedName,
            roomType: roomTypes.room,
            members: chatUserKeys,
        });

        const handleRoomCreated = async (chatSessionKey: string) => {
            let meetingCode: string;
            try {
                meetingCode = await dispatch(
                    getMeetingCode(
                        chatSessionKey,
                        MeetingType.ROOM_FOR_LATER.key,
                    ),
                );
                if (!meetingCode) {
                    handleCreateError('snackbar.error');
                    return;
                }
                /**
                 * requestPin must run AFTER fetchLobbyData: both write
                 * lobby.pin, and a freshly created room has no PIN until
                 * requestPin generates one — so it must be the last writer.
                 */
                await dispatch(
                    fetchLobbyData(chatSessionKey, true, meetingCode),
                );
                await dispatch(requestPin(meetingCode));
            } catch {
                handleCreateError('snackbar.error');
                return;
            }

            // The room itself already exists on the server at this point — a
            // failure here means only its access/host setting didn't save,
            // not that creation failed, so it shouldn't block the user from
            // seeing their new room. Each action already reports its own
            // failure via networkErrorDispatcher, so this doesn't double up.
            try {
                await dispatch(
                    updateMeetingAccess(chatSessionKey, meetingAccess),
                );
                await dispatch(
                    updateRequireHostPresent(
                        chatSessionKey,
                        !letJoinBeforeHost,
                    ),
                );
            } catch {
                // Already reported to the user by the action itself.
            }

            closeModal();
            openModal('roomCreated', {
                roomName: trimmedName,
                meetingCode,
                chatSessionKey,
                meetingAccess,
            });
        };

        dispatch(
            createRoomSession(sessionDTO, null, handleRoomCreated, () =>
                handleCreateError('snackbar.error'),
            ),
        );
    };

    const renderSubmitButtonText = () =>
        submitting ? (
            <Loading size={16} disableTimeout={true} />
        ) : (
            i18next.t('meetings.create_room.submit')
        );

    return (
        <BaseModal
            open={true}
            header={i18next.t('meetings.create_room.title')}
            modalSize={modalSizes.SMALL}
            confirmButtonText={renderSubmitButtonText()}
            disableSubmit={
                !name.trim() ||
                selectedKeys.length === 0 ||
                !ownExtension ||
                submitting
            }
            preventCloseOnSubmit={true}
            onSubmit={handleSubmit}
        >
            <div className="flex flex-col gap-y-4 pb-2">
                <p className="text-sm text-gray-600">
                    {i18next.t('meetings.create_room.activate_description')}
                </p>

                <InputField
                    label={i18next.t('meetings.create_room.name_label')}
                    placeholder={i18next.t(
                        'meetings.create_room.name_placeholder',
                    )}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoFocus={true}
                    maxLength={32}
                />

                <div className="flex flex-col gap-y-1">
                    <p className="text-sm font-bold text-gray-800">
                        {i18next.t('meetings.create_room.members_label')}
                    </p>

                    <ParticipantPicker
                        ariaLabel={i18next.t(
                            'meetings.create_room.members_label',
                        )}
                        selectedKeys={selectedKeys}
                        onChange={setSelectedKeys}
                        ownKey={ownKey}
                    />
                </div>

                <MeetingAccessDropdown
                    value={meetingAccess}
                    onChange={(newMeetingAccess) => {
                        setMeetingAccess(newMeetingAccess);
                        // "No one" means the room isn't open to anyone
                        // arriving on their own yet, so letting them in
                        // before the host does isn't a choice to offer.
                        if (newMeetingAccess === MeetingAccess.NO_ONE.key) {
                            setLetJoinBeforeHost(false);
                        }
                    }}
                />

                <Field as="div" className="flex items-center justify-between">
                    <p
                        className={twMerge(
                            'text-sm text-gray-600',
                            meetingAccess === MeetingAccess.NO_ONE.key &&
                                'text-gray-400',
                        )}
                    >
                        {i18next.t(
                            'meetings.create_room.host_presence_toggle_label',
                        )}
                    </p>
                    <div
                        className={
                            meetingAccess === MeetingAccess.NO_ONE.key
                                ? 'opacity-40'
                                : ''
                        }
                    >
                        <Toggle
                            value={letJoinBeforeHost}
                            onChange={setLetJoinBeforeHost}
                            disabled={
                                meetingAccess === MeetingAccess.NO_ONE.key
                            }
                            text={i18next.t(
                                'meetings.create_room.host_presence_toggle_label',
                            )}
                        />
                    </div>
                    {meetingAccess === MeetingAccess.NO_ONE.key ? (
                        // Why the switch is disabled, for screen readers only:
                        // the greyed row already shows it to sighted users.
                        <Description className="sr-only">
                            {i18next.t(
                                'meetings.create_room.host_presence_toggle_disabled_reason',
                            )}
                        </Description>
                    ) : null}
                </Field>
            </div>
        </BaseModal>
    );
};

export default CreateMeetingRoomModal;
